import { useCallback } from "react";

import { useParams } from "react-router-dom";

import Box from "@/components/Box";
import CircleButton from "@/components/CircleButton";
import { DownloadCrossOriginFile } from "@/components/Links";
import { getAnswerColor } from "@/components/QuestionnaireReport";
import RichText from "@/components/RichText";
import StackedTestProductCard from "@/components/StackedTestProductCard";
import RequestQuestionnaireModal from "@/components/patientView/questionnaire/RequestQuestionnaireModal";
import { USER_QUESTIONNAIRE_REPORT_QUERY } from "@/components/patientView/questionnaire/graphql/queries";
import { PotentialSymptomsRow } from "@/components/reports/InterpretationSharedRows";
import MarkersAssessed from "@/components/reports/MarkersAssessed";
import { useTestProductModalContext } from "@/contexts/TestProductModalContext";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { COMING_SOON, OUT_OF_STOCK } from "@/core/constants";
import useDisclosure from "@/hooks/useDisclosure";
import PenIcon from "@/images/PenIcon";
import Center from "@/tpo/Center";
import { GilroyFontCurrency } from "@/tpo/Currency";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import TestProductInformationPanels from "@/tpo/shop/TestProductInformationPanels";
import { DISPLAY_CONTEXT_ALWAYS_VISIBLE, DISPLAY_CONTEXT_B2B_ONLY } from "@/tpo/shop/constants";
import ButtonV2 from "@/v2/Buttons";
import { useQuery } from "@apollo/client";
import { endsWith } from "@/utils/nodash";

export default function LockedOverviewTabContent({ report, markersAssessed = [] }) {
  const { selectTestProductId } = useTestProductModalContext();
  const { basket, hasBasketGotProduct, removeProductFromBasket, addProductToBasket } =
    useOrganisationBasketContext();
  const { patientId } = useParams();
  const { organisationId } = useOrganisationContext();
  const [showModal, { open: openModal, close: closeModal }] = useDisclosure();
  const { data } = useQuery(USER_QUESTIONNAIRE_REPORT_QUERY, {
    variables: {
      userId: patientId,
      organisationId
    },
    skip: !patientId || !organisationId
  });
  const hasGuardian =
    data?.user?.platformUserProfile?.isWard && !!data?.user?.platformUserProfile?.guardian;
  const isActive = hasGuardian
    ? data?.user?.platformUserProfile?.guardian?.user?.isActive
    : data?.user?.isActive;

  const sampleReport = report.purchaseProduct?.sampleLabReports?.[0];
  const allowMoreThanOne = basket?.purchaseAsStock || basket?.multiplePatientsEnabled;
  let addText = "Add";

  if (
    report.purchaseProduct?.options?.length ||
    (!allowMoreThanOne && report.purchaseProduct?.addons?.length)
  ) {
    addText = "Options";
  } else if (!allowMoreThanOne && hasBasketGotProduct(report.id)) {
    addText = "Added";
  }

  if (report.purchaseProduct?.status === OUT_OF_STOCK) {
    addText = "Out of stock";
  } else if (report.purchaseProduct?.status === COMING_SOON) {
    addText = "Coming soon";
  }

  const handleAddToBasket = useCallback(() => {
    if (!basket) return;
    if (
      basket.multiplePatientsEnabled &&
      report.purchaseProduct?.isCyrex &&
      !hasBasketGotProduct(report.purchaseProduct?.id)
    ) {
      return;
    }
    addProductToBasket(report.purchaseProduct?.id, undefined, undefined, {
      patientId
    });
  }, [basket, report, addProductToBasket, patientId, hasBasketGotProduct]);

  const handleRemoveFromBasket = useCallback(() => {
    const line = basket?.productLineItems?.find(
      lineItem => lineItem.product.id === report.purchaseProduct?.id
    );
    if (!line) return;
    removeProductFromBasket(line.product.id, line.productOption?.id, line.clinicLocation?.id);
  }, [basket, removeProductFromBasket, report.purchaseProduct?.id]);

  const addDisabled =
    report.purchaseProduct?.status === OUT_OF_STOCK ||
    report.purchaseProduct?.status === COMING_SOON ||
    (!allowMoreThanOne && hasBasketGotProduct(report.purchaseProduct?.id)) ||
    (basket?.multiplePatientsEnabled &&
      report.purchaseProduct?.isCyrex &&
      !hasBasketGotProduct(report.purchaseProduct?.id));

  const onAdd =
    allowMoreThanOne || (!allowMoreThanOne && !hasBasketGotProduct(report.purchaseProduct?.id))
      ? e => {
          e.onAdd = true;
          if (allowMoreThanOne || !hasBasketGotProduct(report.purchaseProduct?.id)) {
            handleAddToBasket();
          }
        }
      : undefined;

  const onRemove =
    !allowMoreThanOne && hasBasketGotProduct(report.purchaseProduct?.id)
      ? e => {
          e.onRemove = true;
          handleRemoveFromBasket();
        }
      : undefined;
  const purchaseProductInformationPanels =
    report?.purchaseProduct?.content?.informationPanels?.filter(
      panel =>
        panel.displayContext === DISPLAY_CONTEXT_ALWAYS_VISIBLE ||
        panel.displayContext === DISPLAY_CONTEXT_B2B_ONLY
    ) || [];

  return (
    <Stack gap={40} maxWidth={1024} mx="auto" width="100%">
      <Box maxWidth={658} mx="auto">
        <RichText
          fontFamily="gilroyMedium"
          fontSize={[14, 14, 16]}
          lineHeight={["24px", "24px", "28px"]}
        >
          {report?.purchaseProduct?.content?.description}
        </RichText>
        {sampleReport && (
          <DownloadCrossOriginFile
            fileUrl={sampleReport.pdf}
            fileName={
              endsWith(sampleReport.filename, ".pdf")
                ? sampleReport.filename
                : `${sampleReport.filename}.pdf`
            }
            contentType="application/pdf"
            trigger={
              <ButtonV2 color="dark" withChevron mt={28}>
                View sample report
              </ButtonV2>
            }
          />
        )}
        <ButtonV2 color="dark" withChevron mt={[20, 20, 28]}>
          View sample report
        </ButtonV2>
      </Box>
      <Box maxWidth={720} mx="auto" width="100%">
        <StackedTestProductCard.Card
          onClick={e => {
            if (e.onAdd || e.onRemove) return;
            if (report?.purchaseProduct?.status === COMING_SOON) return;
            selectTestProductId(report.purchaseProduct?.id);
          }}
          data-component-name="ReportListCard"
        >
          <Stack gap={16} pb={60}>
            <StackedTestProductCard.Header
              iconSize={[40, 40, 80]}
              iconType={report?.purchaseProduct?.content?.reportIcon}
              id={report?.purchaseProduct?.id}
              name={report?.purchaseProduct?.name}
            >
              <CircleButton />
            </StackedTestProductCard.Header>
            <StackedTestProductCard.Content
              shortDescription={report?.purchaseProduct?.content?.shortDescription}
              testType={report?.purchaseProduct?.content?.testType}
              categories={report?.purchaseProduct?.content?.categories || []}
            />
          </Stack>
          <Group
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="center"
            mt="auto"
            gap={28}
          >
            <Stack>
              <Group>
                {report?.purchaseProduct?.tradeFullPrice !== undefined &&
                  report?.purchaseProduct?.tradeFullPrice >
                    report?.purchaseProduct?.tradeCurrentPrice && (
                    <GilroyFontCurrency
                      value={report?.purchaseProduct?.tradeFullPrice}
                      symbol={basket?.currencySymbol}
                      strikethrough
                      color="dark"
                    />
                  )}
                <GilroyFontCurrency
                  value={report?.purchaseProduct?.tradeCurrentPrice}
                  symbol={basket?.currencySymbol}
                  color={
                    report?.purchaseProduct?.tradeFullPrice !== undefined &&
                    report?.purchaseProduct?.tradeFullPrice >
                      report?.purchaseProduct?.tradeCurrentPrice
                      ? "red"
                      : "dark"
                  }
                />
              </Group>
              <GilroyFontCurrency
                prefix="RRP"
                color="midGrey"
                fontSize={[16, 16, 18]}
                decimalFontSize={[16, 16, 18]}
                symbol={basket?.currencySymbol}
                value={report?.purchaseProduct?.exvatFullPrice}
              />
            </Stack>
            {onAdd && (
              <ButtonV2
                withChevron
                onClick={onAdd}
                disabled={addDisabled}
                color={addDisabled ? "midGrey" : "green"}
                size={["sm", "sm", "md"]}
              >
                {addText}
              </ButtonV2>
            )}
            {onRemove && (
              <ButtonV2 onClick={onRemove} color="red" withChevron size={["sm", "sm", "md"]}>
                Remove
              </ButtonV2>
            )}
          </Group>
        </StackedTestProductCard.Card>
      </Box>
      <Box
        bg="partners"
        borderRadius={20}
        pt={[40, 40, 60]}
        pb={[60, 60, 80]}
        px={[20, 20, 40]}
        maxWidth={720}
        mx="auto"
      >
        <Stack gap={0}>
          <Center
            flexDirection={["row", "row", "column"]}
            gap={16}
            alignItems={["flex-start", "flex-start", "center"]}
          >
            <PenIcon size={[40, 40, 80]} fill="#1A1A1A" withCircle />
            <Box
              fontFamily="gilroyBold"
              fontSize={[24, 24, 28]}
              lineHeight={["28px", "28px", "36px"]}
            >
              Patient symptom questionnaire
            </Box>
          </Center>
          <Box
            fontFamily="gilroyMedium"
            fontSize={[14, 14, 16]}
            lineHeight={["24px", "24px", "28px"]}
            mt={28}
          >
            The symptom questionnaire gives you additional context by capturing how your patient is
            currently feeling in areas linked to methylation and detox. Their input helps you refine
            your assessment and tailor recommendations with greater accuracy.
            <br />
            <br /> Send the questionnaire for your patient to complete
          </Box>
          <ButtonV2 color="dark" withChevron mt={60} mr="auto" onClick={openModal}>
            Send questionnaire
          </ButtonV2>
        </Stack>
      </Box>
      <MarkersAssessed markers={markersAssessed} />
      {!!purchaseProductInformationPanels.length && (
        <Box maxWidth={1024} mx="auto" width="100%">
          <TestProductInformationPanels panels={purchaseProductInformationPanels} />
        </Box>
      )}
      <Box maxWidth={1024} mx="auto" width="100%" pt={60} pb={80} px={[20, 20, 40]} bg='white' borderRadius={20}>
        <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]} lineHeight={["32px", "32px", "46px"]}>
          Symptoms covered
        </Box>
        <Box
          mt="8px"
          fontFamily="gilroyMedium"
          fontSize={[14, 14, 16]}
          lineHeight={["24px", "24px", "28px"]}
        >
          The below tests all include markers that are considered in this report
        </Box>
        <Stack gap={0} mt={[24, 24, 40]}>
          {report?.questionsAndAnswers?.map(question => (
            <PotentialSymptomsRow
              key={question.id}
              title={question.question.symptomGroup}
              risk={question.answerText || "No Answer"}
              color={
                question.answerScore != null ? getAnswerColor(question.answerScore) : "midGrey"
              }
            />
          ))}
        </Stack>
      </Box>
      <RequestQuestionnaireModal
        show={showModal}
        onClose={closeModal}
        patientId={patientId}
        organisationId={organisationId}
        isActive={isActive}
        hasGuardian={hasGuardian}
      />
    </Stack>
  );
}
